/**
 * DetailTabs — a generic, page-agnostic tab wrapper over the shadcn/Radix
 * `tabs.jsx` primitive, themed for the Vantio dark palette (orange #fb923c
 * active accent) with a horizontally scrollable tab bar on mobile.
 *
 * Mount semantics (intentional, see CLAUDE.md Regel 38 + Unit-Detail
 * tab-refactor discovery):
 *   - Default = mount-all (Option A). Every panel mounts on first render and
 *     stays mounted; inactive panels are hidden via the CSS class
 *     `data-[state=inactive]:hidden`. NOTE: Radix's native `hidden` attribute
 *     is NOT used for gating — with `forceMount`, Radix keeps `present` true
 *     so `hidden` is always false (verified in @radix-ui/react-tabs). The
 *     `data-state` attribute IS always set, so we gate on it via CSS. This
 *     keeps child fetches/effects from re-firing on tab switch.
 *   - Per-tab opt-in `lazyMount: true` defers a panel's children until the
 *     tab is first activated (becomes the active value), then keeps them
 *     mounted. Used for tabs whose children measure their container width and
 *     would render at zero size under `display:none`.
 *
 * This component is STRICTLY page-agnostic: it knows only tabs metadata, the
 * active value, mount mode, and the generic theme. Labels/icons arrive via
 * the `tabs` prop; panel content arrives via <DetailTabPanel> children. No
 * domain concept (units, rooms, tenants, …) lives here, so it is reusable
 * across detail pages unchanged.
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const DetailTabsContext = React.createContext(null);

// Active-tab accent classes, written LITERALLY per accent so Tailwind's JIT
// sees the full strings (never build `text-[${x}]` dynamically — it gets
// purged). "orange" is the default and is byte-identical to the original
// active-accent string; "blue" (#5b9cf6) matches the tenant-detail palette.
// "primary" is built from theme tokens only (see INACTIVE_CLASSES).
const ACCENT_CLASSES = {
  orange:
    "data-[state=active]:text-[#fb923c] data-[state=active]:border-[#fb923c] data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  blue:
    "data-[state=active]:text-[#5b9cf6] data-[state=active]:border-[#5b9cf6] data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  primary:
    "data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none",
};

// Inactive-tab classes per accent. "orange" and "blue" keep the original
// literal palette byte for byte (moved here, not changed). Two things about
// that palette are why "primary" exists: in light mode a global `!important`
// remap in index.css repaints its slate grey on every tab, the active one
// included, and in dark mode the `dark:` colour is emitted after the
// `data-[state=active]:` one and wins — so neither theme shows the active
// tab in its accent, and the dark grey measures 4.39:1 on a card, under
// WCAG AA. Theme tokens switch with the theme, so "primary" needs no `dark:`
// variant and nothing remaps them. Its focus ring is drawn INSIDE the
// trigger: the tab list scrolls horizontally, and a scroll container clips
// whatever is drawn outside it — the default offset ring lost its top and
// bottom.
const LEGACY_INACTIVE_CLASSES = "text-[#64748b] dark:text-[#6b7a9a]";
const INACTIVE_CLASSES = {
  orange: LEGACY_INACTIVE_CLASSES,
  blue: LEGACY_INACTIVE_CLASSES,
  primary:
    "text-muted-foreground hover:text-foreground focus-visible:ring-inset focus-visible:ring-offset-0",
};

function DetailTabs({
  tabs,
  value,
  onValueChange,
  children,
  className,
  listClassName,
  accent = "orange",
  ...props
}) {
  const accentClasses = ACCENT_CLASSES[accent] ?? ACCENT_CLASSES.orange;
  const inactiveClasses = INACTIVE_CLASSES[accent] ?? INACTIVE_CLASSES.orange;
  // Tracks which tabs have ever been activated, so lazy panels render once
  // opened and then persist. Seeded with the initial value.
  const [activated, setActivated] = React.useState(() => new Set([value]));

  React.useEffect(() => {
    setActivated((prev) => {
      if (prev.has(value)) return prev;
      const next = new Set(prev);
      next.add(value);
      return next;
    });
  }, [value]);

  const tabsById = React.useMemo(() => {
    const map = {};
    for (const t of tabs || []) map[t.id] = t;
    return map;
  }, [tabs]);

  const ctx = React.useMemo(
    () => ({ value, activated, tabsById }),
    [value, activated, tabsById]
  );

  return (
    <Tabs
      value={value}
      onValueChange={onValueChange}
      className={cn("w-full", className)}
      {...props}
    >
      <TabsList
        className={cn(
          "flex w-full overflow-x-auto flex-nowrap justify-start gap-1 h-auto p-0 bg-transparent rounded-none border-b border-black/10 dark:border-white/[0.08]",
          listClassName
        )}
      >
        {(tabs || []).map((t) => {
          const Icon = t.icon;
          return (
            <TabsTrigger
              key={t.id}
              value={t.id}
              className={cn(
                "shrink-0 whitespace-nowrap rounded-none border-b-2 border-transparent -mb-px px-4 py-2.5 text-[13px] font-medium bg-transparent shadow-none transition",
                inactiveClasses,
                accentClasses
              )}
            >
              {Icon ? <Icon className="h-4 w-4 mr-2 shrink-0" /> : null}
              {t.label}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <DetailTabsContext.Provider value={ctx}>
        {children}
      </DetailTabsContext.Provider>
    </Tabs>
  );
}

function DetailTabPanel({ tabId, children, className, ...props }) {
  const ctx = React.useContext(DetailTabsContext);
  const value = ctx ? ctx.value : undefined;
  const activated = ctx ? ctx.activated : null;
  const tab = ctx && ctx.tabsById ? ctx.tabsById[tabId] : undefined;
  const lazy = !!(tab && tab.lazyMount);

  const shouldRender =
    !lazy || value === tabId || (activated ? activated.has(tabId) : false);

  return (
    <TabsContent
      value={tabId}
      forceMount
      className={cn("mt-6 data-[state=inactive]:hidden", className)}
      {...props}
    >
      {shouldRender ? children : null}
    </TabsContent>
  );
}

export { DetailTabs, DetailTabPanel };
